import { supabase } from './supabase';
import { useAuth } from '../auth/AuthContext';

/**
 * Creates a new lead in the database
 * @param {Object} leadData - The lead data to create
 * @returns {Promise<Object>} The created lead
 */
export const createLead = async (leadData) => {
  try {
    // Get the current user if available
    let userId = null;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        userId = user.id;
      }
    } catch (error) {
      console.warn('User not authenticated, creating anonymous lead');
    }

    // Prepare lead data - only include fields that exist in the schema
    // Based on the schema: id, name, email, phone, item_type, description, is_buyer, status, created_at, updated_at, user_id, assigned_to_id, lead_owner_type
    const lead = {
      name: leadData.name || 'Anonymous',
      email: leadData.email || 'anonymous@example.com',
      phone: leadData.phone || '',
      item_type: leadData.lead_type || 'auto', // Using item_type as it exists in the schema
      description: leadData.additional_details || '',
      is_buyer: true,
      status: 'new',
      // If we have a user ID, assign the lead to them
      ...(userId ? { user_id: userId } : {}),
      lead_owner_type: 'buyer'
    };

    console.log('Submitting lead data:', lead);
    
    // Insert the lead into the database
    const { data, error } = await supabase
      .from('leads')
      .insert([lead])
      .select();

    if (error) {
      console.error('Error inserting lead:', error);
      throw error;
    }
    
    console.log('Lead created successfully:', data);
    return { success: true, data: data[0] };
  } catch (error) {
    console.error('Error creating lead:', error);
    return { success: false, error };
  }
};

/**
 * Gets all leads for the current user
 * @returns {Promise<Array>} Array of leads
 */
export const getUserLeads = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Get all leads assigned to this user
    // Using user_id instead of assigned_to since that's what's in the schema
    const { data, error } = await supabase
      .from('leads')
      .select('*')
      .eq('user_id', user.id);
    
    if (error) throw error;
    
    return { success: true, data };
  } catch (error) {
    console.error('Error getting user leads:', error);
    return { success: false, error };
  }
};

export default {
  createLead,
  getUserLeads
};
